import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Skeleton } from '../../lib/shadcn/skeleton'
import { Boxes } from 'lucide-react'

export interface TopProductPoint {
  product: string
  quantity: number
}

const COLORS = [
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-1))',
  'hsl(var(--chart-5))',
]

export default function TopProductsChart({
  data,
  loading,
}: {
  data: TopProductPoint[]
  loading: boolean
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Boxes className="w-4 h-4" />
          Top products
        </CardTitle>
        <CardDescription>By total units ordered</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-[320px] w-full" />
        ) : data.length === 0 ? (
          <p className="py-16 text-sm text-muted-foreground text-center">No order lines yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(320, data.length * 44 + 48)}>
            <BarChart data={data} layout="vertical" margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.65} stroke="hsl(var(--border))" horizontal={false} />
              <XAxis axisLine={false} tickLine={false} tickMargin={10}
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <YAxis axisLine={false} tickLine={false} tickMargin={10}
                type="category"
                dataKey="product"
                width={120}
                tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
              />
              <Tooltip
                formatter={(value: unknown): [string, string] => [`${value} units`, 'Ordered']}
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  color: 'hsl(var(--popover-foreground))',
                }}
              />
              <Bar dataKey="quantity" radius={[0, 4, 4, 0]}>
                {data.map((entry, index) => (
                  <Cell key={entry.product} fill={COLORS[index % COLORS.length] ?? 'hsl(var(--chart-2))'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
